// adminApi.ts — typed wrappers for the Slice-2 admin services:
// PlatformOrgService, PlatformUserService, PlatformNotifierService,
// PlatformPaymentsService. Shares the adminCall transport from api.ts
// (auth headers + 401 handling).

import { adminCall, getAdminToken } from './api';

// ───── Shared types ─────

export interface PageInfo {
  limit?: number;
  offset?: number;
}

// ───── Orgs ─────

export interface OrgListItem {
  id: string;
  name: string;
  slug: string;
  shortId: string;
  type: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  ownerEmail: string;
  memberCount: number | string;
  eventCount: number | string;
  mediaCount: number | string;
  storageBytes: number | string;
  createdAt: string;
}

export interface OrgDetail extends OrgListItem {
  stripeCustomerId: string;
  brandingJson: string;
  settingsJson: string;
  paymentSettingsJson: string;
  updatedAt: string;
  ownerUserId: string;
  ownerName: string;
  ordersCount30d: number | string;
  openPaymentEvents: number | string;
  stuckPipelineCount: number | string;
}

export interface Member {
  userId: string;
  email: string;
  name: string;
  role: string;
  invitedAt: string;
  acceptedAt?: string;
  lastLoginAt?: string;
}

export interface EventNodeRow {
  id: string;
  name: string;
  slug: string;
  shortId: string;
  nodeType: string;
  path: string;
  depth: number;
  galleryState: string;
  publishedAt?: string;
  mediaCount: number | string;
  createdAt: string;
}

export interface StorageTierTotal {
  tier: string;
  mediaCount: number | string;
  bytes: number | string;
}
export interface StorageVisibilityTotal {
  galleryState: string;
  mediaCount: number | string;
  bytes: number | string;
}
export interface StorageBreakdown {
  totalBytes: number | string;
  totalMedia: number | string;
  byTier: StorageTierTotal[];
  byVisibility: StorageVisibilityTotal[];
}

export interface GatewayConnection {
  id: string;
  gateway: string;
  mode: string;
  publishableKey: string;
  active: boolean;
  lastVerifiedAt?: string;
  verifyError?: string;
  connectedAt: string;
  connectedByUser: string;
  events30d: number | string;
}

export interface PipelineStageCount {
  stage: string;
  count: number | string;
}
export interface StuckMedia {
  id: string;
  filename: string;
  pipelineState: string;
  lastEventAt: string;
  eventNodeId: string;
  eventName: string;
}
export interface PipelineHealth {
  stageCounts: PipelineStageCount[];
  stuck: StuckMedia[];
  stuckThresholdSeconds: number | string;
}

export interface OffboardingSchedule {
  orgId: string;
  scheduledAt: string;
  scheduledByPlatformUserId: string;
  scheduledByUsername: string;
  reason: string;
  createdAt: string;
  cancelledAt?: string;
  cancelledByPlatformUserId?: string;
  cancelledByUsername?: string;
  executedAt?: string;
  notes?: string;
}

export interface FeatureFlag {
  id: string;
  orgId: string;
  flagKey: string;
  valueJson: string;
  setByPlatformUserId: string;
  setByUsername: string;
  setAt: string;
  expiresAt?: string;
  notes?: string;
}

export const platformOrgs = {
  list: (req: {
    search?: string;
    subscriptionTier?: string;
    subscriptionStatus?: string;
    type?: string;
    sort?: string;
    limit?: number;
    offset?: number;
  }) =>
    adminCall<{ orgs: OrgListItem[]; total: number | string }>(
      'momentpix.admin.v1.PlatformOrgService',
      'ListOrgs',
      req,
    ),
  get: (orgId: string) =>
    adminCall<{ org: OrgDetail }>('momentpix.admin.v1.PlatformOrgService', 'GetOrg', { orgId }),
  members: (orgId: string) =>
    adminCall<{ members: Member[] }>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgMembers',
      { orgId },
    ),
  events: (orgId: string, req: PageInfo = {}) =>
    adminCall<{ events: EventNodeRow[]; total: number | string }>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgEvents',
      { orgId, ...req },
    ),
  storage: (orgId: string) =>
    adminCall<StorageBreakdown>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgStorageBreakdown',
      { orgId },
    ),
  gateway: (orgId: string) =>
    adminCall<{ connections: GatewayConnection[] }>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgGatewayStatus',
      { orgId },
    ),
  pipelineHealth: (orgId: string) =>
    adminCall<PipelineHealth>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgPipelineHealth',
      { orgId },
    ),

  // ── Mutations (Slice 4a) ──
  suspend: (orgId: string, reason: string) =>
    adminCall<{ ok: boolean }>('momentpix.admin.v1.PlatformOrgService', 'SuspendOrg', { orgId, reason }),
  unsuspend: (orgId: string, reason?: string) =>
    adminCall<{ ok: boolean }>('momentpix.admin.v1.PlatformOrgService', 'UnsuspendOrg', { orgId, reason }),
  scheduleOffboard: (orgId: string, reason: string, graceDays: number) =>
    adminCall<{ schedule: OffboardingSchedule }>(
      'momentpix.admin.v1.PlatformOrgService',
      'ScheduleOffboard',
      { orgId, reason, graceDays },
    ),
  cancelOffboard: (orgId: string, reason?: string) =>
    adminCall<{ schedule: OffboardingSchedule }>(
      'momentpix.admin.v1.PlatformOrgService',
      'CancelOffboard',
      { orgId, reason },
    ),
  getOffboarding: (orgId: string) =>
    adminCall<{ schedule?: OffboardingSchedule }>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgOffboarding',
      { orgId },
    ),

  getFeatureFlags: (orgId: string) =>
    adminCall<{ flags: FeatureFlag[] }>(
      'momentpix.admin.v1.PlatformOrgService',
      'GetOrgFeatureFlags',
      { orgId },
    ),
  setFeatureFlag: (req: {
    orgId: string;
    flagKey: string;
    valueJson: string;
    expiresAt?: string;
    notes?: string;
  }) =>
    adminCall<{ flag: FeatureFlag }>(
      'momentpix.admin.v1.PlatformOrgService',
      'SetOrgFeatureFlag',
      req,
    ),
  deleteFeatureFlag: (orgId: string, flagKey: string) =>
    adminCall<{ ok: boolean }>(
      'momentpix.admin.v1.PlatformOrgService',
      'DeleteOrgFeatureFlag',
      { orgId, flagKey },
    ),

  // ── Membership overrides (emergency platform-staff path) ──
  // See docs/flows/TEAM-MANAGEMENT.md §"Platform-Admin Override".
  // Step-up gated; runWithStepUp + StepUpModal pattern at the call site.
  updateMembershipRole: (orgId: string, userId: string, newRole: string) =>
    adminCall<{}>(
      'momentpix.admin.v1.PlatformOrgService',
      'UpdateOrgMembershipRole',
      { orgId, userId, newRole },
    ),
  removeMembership: (orgId: string, userId: string) =>
    adminCall<{}>(
      'momentpix.admin.v1.PlatformOrgService',
      'RemoveOrgMembership',
      { orgId, userId },
    ),

  create: (req: {
    name: string;
    slug: string;
    ownerEmail: string;
    ownerName: string;
    welcomeNote?: string;
  }) =>
    adminCall<{
      orgId: string;
      ownerUserId: string;
      claimUrl: string;
      claimExpiresAt: string;
    }>('momentpix.admin.v1.PlatformOrgService', 'CreateOrganization', req),
};

export const platformOps = {
  requeue: (mediaId: string, reason: string, restartStage = 'ingest') =>
    adminCall<{ mediaId: string; newState: string; subject: string }>(
      'momentpix.admin.v1.PlatformOpsService',
      'RequeuePipelineItem',
      { mediaId, reason, restartStage },
    ),
};

// ───── Users ─────

export type UserKind =
  | 'USER_KIND_PHOTOGRAPHER'
  | 'USER_KIND_CONSUMER'
  | 'USER_KIND_PLATFORM'
  | 'USER_KIND_UNSPECIFIED';

export interface UserOrgMembership {
  orgId: string;
  orgName: string;
  role: string;
}

export interface UserSearchResult {
  kind: UserKind;
  id: string;
  email: string;
  name: string;
  status: string;
  lastLoginAt?: string;
  platformRole?: string;
  orgMemberships?: UserOrgMembership[];
}

export interface PlatformUserRow {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  status: string;
  totpEnrolled: boolean;
  totpEnrolledAt?: string;
  lastLoginAt?: string;
  lastLoginIp?: string;
  failedLoginCount: number;
  lockedUntil?: string;
  createdAt: string;
  createdById?: string;
  disabledAt?: string;
  notes?: string;
}

export const platformUsers = {
  search: (query: string, limit = 25) =>
    adminCall<{ results: UserSearchResult[]; totalEstimate: number | string }>(
      'momentpix.admin.v1.PlatformUserService',
      'SearchUsers',
      { query, limit },
    ),
  get: (kind: UserKind, id: string) =>
    adminCall<{ user: UserSearchResult }>('momentpix.admin.v1.PlatformUserService', 'GetUser', {
      kind,
      id,
    }),

  // ── Platform user management (Slice 4a) ──
  listPlatform: (includeDisabled = false) =>
    adminCall<{ users: PlatformUserRow[] }>(
      'momentpix.admin.v1.PlatformUserService',
      'ListPlatformUsers',
      { includeDisabled },
    ),
  createPlatform: (req: {
    username: string;
    email: string;
    name: string;
    initialPassword: string;
    role: string;
    notes?: string;
  }) =>
    adminCall<{ user: PlatformUserRow }>(
      'momentpix.admin.v1.PlatformUserService',
      'CreatePlatformUser',
      req,
    ),
  disablePlatform: (id: string, reason: string) =>
    adminCall<{ user: PlatformUserRow }>(
      'momentpix.admin.v1.PlatformUserService',
      'DisablePlatformUser',
      { id, reason },
    ),
  enablePlatform: (id: string, reason?: string) =>
    adminCall<{ user: PlatformUserRow }>(
      'momentpix.admin.v1.PlatformUserService',
      'EnablePlatformUser',
      { id, reason },
    ),
  updatePlatformRole: (id: string, newRole: string, reason: string) =>
    adminCall<{ user: PlatformUserRow }>(
      'momentpix.admin.v1.PlatformUserService',
      'UpdatePlatformUserRole',
      { id, newRole, reason },
    ),
  resetPlatformTOTP: (id: string, reason: string) =>
    adminCall<{ user: PlatformUserRow }>(
      'momentpix.admin.v1.PlatformUserService',
      'ResetPlatformUserTOTP',
      { id, reason },
    ),
};

// ───── Notifier ─────

export interface NotificationSummary {
  id: string;
  orgId?: string;
  consumerId?: string;
  userId?: string;
  notificationType: string;
  channel: string;
  recipientAddress: string;
  subject: string;
  status: string;
  retentionClass: string;
  createdAt: string;
  sentAt?: string;
  deliveredAt?: string;
  failedAt?: string;
  failureReason?: string;
  parentNotificationId?: string;
  providerMessageId?: string;
}

export interface NotificationDetail {
  summary: NotificationSummary;
  body: string;
  renderedBody: string;
  metadataJson: string;
}

export const platformNotifier = {
  query: (req: {
    orgId?: string;
    consumerId?: string;
    userId?: string;
    notificationType?: string;
    channel?: string;
    status?: string;
    recipientContains?: string;
    sinceRfc3339?: string;
    untilRfc3339?: string;
    limit?: number;
  }) =>
    adminCall<{ rows: NotificationSummary[]; hasMore: boolean }>(
      'momentpix.admin.v1.PlatformNotifierService',
      'QueryNotifications',
      req,
    ),
  get: (id: string) =>
    adminCall<{ notification: NotificationDetail }>(
      'momentpix.admin.v1.PlatformNotifierService',
      'GetNotification',
      { id },
    ),
  resend: (id: string, reason: string) =>
    adminCall<{ resent: NotificationSummary }>(
      'momentpix.admin.v1.PlatformNotifierService',
      'ResendNotification',
      { id, reason },
    ),
};

// ───── Payments ─────

export interface PaymentEventRow {
  id: string;
  gateway: string;
  providerEventId: string;
  eventType: string;
  sessionId?: string;
  orderId?: string;
  amountCents?: number | string;
  currency?: string;
  orgId?: string;
  orgName?: string;
  receivedAt: string;
  processedAt?: string;
  processError?: string;
}

export const platformPayments = {
  query: (req: {
    orgId?: string;
    gateway?: string;
    eventTypePrefix?: string;
    failedOnly?: boolean;
    unprocessedOnly?: boolean;
    sinceRfc3339?: string;
    untilRfc3339?: string;
    limit?: number;
  }) =>
    adminCall<{ rows: PaymentEventRow[] }>(
      'momentpix.admin.v1.PlatformPaymentsService',
      'QueryPaymentEvents',
      req,
    ),
  get: (id: string) =>
    adminCall<{ row: PaymentEventRow; rawPayload: string }>(
      'momentpix.admin.v1.PlatformPaymentsService',
      'GetPaymentEvent',
      { id },
    ),
};

// ───── helpers ─────

export function formatBytes(b: number | string | undefined): string {
  const n = typeof b === 'string' ? Number(b) : b || 0;
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatInt(n: number | string | undefined): string {
  const v = typeof n === 'string' ? Number(n) : n || 0;
  return v.toLocaleString();
}

export function formatCents(n: number | string | undefined): string {
  const v = typeof n === 'string' ? Number(n) : n || 0;
  return `$${(v / 100).toFixed(2)}`;
}

// ─── Notifications V1 — catalog + provider mgmt + routing + send-test ──

export interface NotificationTypeSpec {
  typeId: string;
  displayName: string;
  category: string;
  triggerDescription: string;
  complianceClass: string;
  priority: string;
  userOptOut: string;
  supportedChannels: string[];
  requiredVars: string[];
  retentionClass: string;
  samplePayloadJson: string;
  // True when the platform actually has a sender call site for this
  // type today. The catalog UI uses this to disable Send Test and the
  // routing rule editor uses it to refuse binding unwired types.
  wired: boolean;
}

export interface CredentialField {
  key: string;
  label: string;
  type: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  selectOptions?: string[];
  secret: boolean;
}

export interface ProviderType {
  id: string;
  displayName: string;
  helpUrl?: string;
  credentialSchema: CredentialField[];
}

export interface Provider {
  id: string;
  providerType: string;
  name: string;
  enabled: boolean;
  healthStatus: string;
  lastHealthCheckAt?: string;
  lastErrorAt?: string;
  lastErrorMessage?: string;
  consecutiveFailures: number;
  configJson: string;
  createdAt: string;
}

export interface RoutingRule {
  id: string;
  notificationType: string; // empty = catch-all
  primaryProviderId: string;
  fallbackProviderIds: string[];
  enabled: boolean;
}

export const platformNotifications = {
  listCatalog: (category?: string) =>
    adminCall<{ types: NotificationTypeSpec[] }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'ListCatalog',
      { category: category || '' },
    ),
  getNotificationType: (typeId: string) =>
    adminCall<{ spec: NotificationTypeSpec }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'GetNotificationType',
      { typeId },
    ),
  listProviderTypes: () =>
    adminCall<{ types: ProviderType[] }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'ListProviderTypes',
      {},
    ),
  listProviders: () =>
    adminCall<{ providers: Provider[] }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'ListProviders',
      {},
    ),
  createProvider: (req: { providerType: string; name: string; values: Record<string, string>; enabled?: boolean }) =>
    adminCall<{ provider: Provider }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'CreateProvider',
      { ...req, enabled: req.enabled !== false },
    ),
  updateProvider: (req: { id: string; name?: string; values?: Record<string, string>; enabled?: boolean; setEnabled?: boolean }) =>
    adminCall<{ provider: Provider }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'UpdateProvider',
      req,
    ),
  deleteProvider: (id: string) =>
    adminCall<{}>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'DeleteProvider',
      { id },
    ),
  testProviderConnection: (id: string) =>
    adminCall<{ ok: boolean; message: string; testedAt: string }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'TestProviderConnection',
      { id },
    ),
  listRoutingRules: () =>
    adminCall<{ rules: RoutingRule[] }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'ListRoutingRules',
      {},
    ),
  upsertRoutingRule: (req: { notificationType: string; primaryProviderId: string; fallbackProviderIds: string[]; enabled: boolean }) =>
    adminCall<{ rule: RoutingRule }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'UpsertRoutingRule',
      req,
    ),
  deleteRoutingRule: (notificationType: string) =>
    adminCall<{}>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'DeleteRoutingRule',
      { notificationType },
    ),
  sendTestNotification: (notificationType: string, recipientEmail: string) =>
    adminCall<{ ok: boolean; providerUsed: string; providerMessageId: string; errorMessage: string }>(
      'momentpix.admin.v1.PlatformNotificationsService',
      'SendTestNotification',
      { notificationType, recipientEmail },
    ),

  // OAuth start endpoints — non-RPC routes since the response leads to
  // a browser redirect to a third-party (Microsoft) auth page.
  // POST so the bearer token can ride in the Authorization header
  // (browser <a href> redirects can't carry it).
  startMicrosoftOAuth: async (req: { name: string; replaceProviderId?: string }): Promise<string> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const resp = await fetch('/auth/microsoft/start', {
      method: 'POST',
      headers,
      body: JSON.stringify(req),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`OAuth start failed (HTTP ${resp.status}): ${text}`);
    }
    const data = await resp.json();
    return data.redirectUrl as string;
  },
};

// ─── PlatformAgreementsService ───────────────────────────────────
//
// See docs/flows/AGREEMENTS.md. Read RPCs (ListKinds, GetVersionHistory)
// gated by CapViewAgreements; PublishVersion gated by
// CapPublishAgreements + step-up.

export interface PolicyVersionSummary {
  id: string;
  kind: string;
  version: number;
  title: string;
  required: boolean;
  targetUserTypes?: string[];
  effectiveAt?: string;     // RFC3339
  supersededAt?: string;    // null/undefined on current
  acceptanceCount: number;
  bodyLengthBytes: number;
  // Markdown source. Populated by ListKinds for the CURRENT version
  // only — used by the publish modal to pre-fill the editor with the
  // live text so opening "Publish new version" = editing existing.
  // Empty/undefined for history rows from GetVersionHistory.
  bodyMd?: string;
}

export interface KindRow {
  kind: string;
  current?: PolicyVersionSummary;   // undefined when no version published
  totalVersions: number;
  acceptanceCountCurrent: number;
}

export const platformAgreements = {
  listKinds: () =>
    adminCall<{ kinds: KindRow[] }>(
      'momentpix.admin.v1.PlatformAgreementsService',
      'ListKinds',
      {},
    ),
  versionHistory: (kind: string) =>
    adminCall<{ versions: PolicyVersionSummary[] }>(
      'momentpix.admin.v1.PlatformAgreementsService',
      'GetVersionHistory',
      { kind },
    ),
  publish: (req: {
    kind: string;
    version: number; // 0 = auto-bump to (max + 1)
    title: string;
    bodyMd: string;
    required: boolean;
    targetUserTypes?: string[];
  }) =>
    adminCall<{ published: PolicyVersionSummary }>(
      'momentpix.admin.v1.PlatformAgreementsService',
      'PublishVersion',
      req,
    ),
};
